'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { newsletterSchema, type NewsletterFormData } from '@/lib/schemas'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiMail, FiCheck, FiAlertCircle } from 'react-icons/fi'

export function NewsletterForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
  })

  const onSubmit = async (data: NewsletterFormData) => {
    setIsSubmitting(true)
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Failed')
      setSubmitMessage(json.message || 'Successfully subscribed to our newsletter!')
      setIsSuccess(true)
      reset()
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      setSubmitMessage(`Error: ${msg}`)
      setIsSuccess(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit(onSubmit)} className="relative">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <FiMail className="w-5 h-5 text-slate-400" />
            </div>
            <input
              {...register('email')}
              type="email"
              placeholder="Enter your email address"
              className={`w-full pl-12 pr-4 py-4 bg-white border-2 rounded-xl outline-none transition-all duration-200 ${
                errors.email 
                  ? 'border-rose-500 focus:border-rose-500' 
                  : 'border-slate-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10'
              }`}
            />
          </div>
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold rounded-xl shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[140px]"
          >
            {isSubmitting ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
              />
            ) : (
              'Subscribe'
            )}
          </motion.button>
        </div>

        <AnimatePresence>
          {errors.email && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-rose-500 text-sm mt-2 flex items-center gap-1"
            >
              <FiAlertCircle className="w-4 h-4" />
              {errors.email.message}
            </motion.p>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {submitMessage && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className={`mt-3 p-3 rounded-lg flex items-center gap-2 text-sm ${
                isSuccess 
                  ? 'bg-teal-50 text-teal-700' 
                  : 'bg-rose-50 text-rose-700'
              }`}
            >
              {isSuccess ? (
                <FiCheck className="w-4 h-4" />
              ) : (
                <FiAlertCircle className="w-4 h-4" />
              )}
              {submitMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </div>
  )
}
